<template>
  <div class="page-container">
    <div class="toolbar">
      <template v-if="grainStoreId">
        <span class="store-tag">当前粮库：<b>{{ storeName || ('#' + grainStoreId) }}</b></span>
        <el-button text @click="back" v-if="!embedded">返回粮库列表</el-button>
      </template>
      <el-button type="primary" @click="openModal">新建仓房</el-button>
    </div>
    <el-table stripe :data="warehouses" border>
      <el-table-column prop="warehouseName" label="仓房名称" />
      <el-table-column prop="area" label="面积(㎡)" />
      <el-table-column prop="capacity" label="容量(吨)" />
      <el-table-column prop="usedCapacity" label="已用容量(吨)" />
      <el-table-column prop="type" label="类型" />
      <el-table-column prop="status" label="状态">
        <template #default="scope">
          <el-tag :type="scope.row.status === 1 ? 'success' : 'info'">{{ scope.row.status === 1 ? '使用中' : scope.row.status === 2 ? '维护中' : '空闲' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间">
        <template #default="scope">
          {{ formatDateTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作">
        <template #default="scope">
          <el-button size="small" @click="editWarehouse(scope.row)">编辑</el-button>
          <el-button size="small" type="danger" @click="handleDeleteWarehouse(scope.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="仓房信息" v-model="dialogVisible" width="600px">
      <el-form :model="formData" label-width="100px">
        <el-form-item label="所属粮库" v-if="!grainStoreId">
          <el-select v-model="formData.grainStoreId" filterable placeholder="选择粮库" style="width:100%">
            <el-option v-for="s in stores" :key="s.id" :label="s.storeName" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="仓房名称">
          <el-input v-model="formData.warehouseName" />
        </el-form-item>
        <el-form-item label="面积(㎡)">
          <el-input v-model="formData.area" type="number" />
        </el-form-item>
        <el-form-item label="容量(吨)">
          <el-input v-model="formData.capacity" type="number" />
        </el-form-item>
        <el-form-item label="已用容量(吨)">
          <el-input v-model="formData.usedCapacity" type="number" />
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="formData.type">
            <el-option label="普通仓" value="普通仓" />
            <el-option label="低温仓" value="低温仓" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="formData.status">
            <el-option label="空闲" :value="0" />
            <el-option label="使用中" :value="1" />
            <el-option label="维护中" :value="2" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveWarehouse">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getWarehouseList, getWarehouseByGrainStoreId, saveWarehouse, deleteWarehouse, getGrainStoreList } from '../api'
import { formatDateTime } from '../utils/format'

const props = defineProps({
  grainStoreId: { type: Number, default: null },
  storeName: { type: String, default: '' },
  embedded: { type: Boolean, default: false }
})
const route = useRoute()
const router = useRouter()
const grainStoreId = computed(() => props.grainStoreId ?? (route.query.grainStoreId ? Number(route.query.grainStoreId) : null))
const storeName = computed(() => props.storeName || route.query.name || '')

const warehouses = ref([])
const stores = ref([])
const dialogVisible = ref(false)
const formData = reactive({
  id: null,
  warehouseName: '',
  grainStoreId: null,
  area: 0,
  capacity: 0,
  usedCapacity: 0,
  type: '普通仓',
  status: 0
})

onMounted(() => {
  loadWarehouses()
  if (!grainStoreId.value) loadStores()
})

const loadWarehouses = async () => {
  try {
    const res = grainStoreId.value
      ? await getWarehouseByGrainStoreId(grainStoreId.value)
      : await getWarehouseList()
    warehouses.value = res.data
  } catch (error) {
    ElMessage.error('加载仓房失败')
  }
}

const loadStores = async () => {
  try {
    const res = await getGrainStoreList()
    stores.value = res.data || []
  } catch (error) {
    console.error('加载粮库失败', error)
  }
}

const openModal = () => {
  Object.assign(formData, {
    id: null, warehouseName: '', grainStoreId: grainStoreId.value, area: 0, capacity: 0, usedCapacity: 0, type: '普通仓', status: 0
  })
  dialogVisible.value = true
}

const editWarehouse = (row) => {
  Object.assign(formData, JSON.parse(JSON.stringify(row)))
  dialogVisible.value = true
}

const handleSaveWarehouse = async () => {
  if (!formData.grainStoreId) {
    ElMessage.error('请选择所属粮库')
    return
  }
  try {
    await saveWarehouse(formData)
    ElMessage.success('保存成功')
    dialogVisible.value = false
    loadWarehouses()
  } catch (error) {
    ElMessage.error('保存失败')
  }
}

const handleDeleteWarehouse = async (id) => {
  try {
    await deleteWarehouse(id)
    ElMessage.success('删除成功')
    loadWarehouses()
  } catch (error) {
    ElMessage.error('删除失败')
  }
}

const back = () => router.push('/grain-store')
</script>

<style scoped>
.store-tag {
  margin-right: 12px;
  font-size: 14px;
  color: #606266;
}
</style>
